// src/pages/finance-portal/stripe-treasury/StripeTreasury.js
//
// Stripe Treasury console — SDK-only (no webhooks). Status updates are
// pulled on demand via the bulk Sync Status buttons.
//
// Tabs:
//   - Transactions          (Treasury.Transaction.list)
//   - Outbound Payments     (vendor / payroll / EWA / tax remittance)
//   - Outbound Transfers    (inter-FA / external transfers)
//   - Inbound Transfers     (funding the FA from external bank)
//   - Received Credits      (incoming wires/ACH credits)
//   - Received Debits       (incoming debits)

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
    Paper, Skeleton, Stack, Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CancelIcon from '@mui/icons-material/Cancel';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import LaunchIcon from '@mui/icons-material/Launch';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val, currency = 'usd') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
    }).format((val || 0) / 100);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
    return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

const STATUS_COLORS = {
    posted: { bg: '#dcfce7', color: '#166534' },
    succeeded: { bg: '#dcfce7', color: '#166534' },
    open: { bg: '#dbeafe', color: '#1e40af' },
    processing: { bg: '#dbeafe', color: '#1e40af' },
    void: { bg: '#fecaca', color: '#991b1b' },
    canceled: { bg: '#f1f5f9', color: '#475569' },
    failed: { bg: '#fee2e2', color: '#991b1b' },
    returned: { bg: '#fef3c7', color: '#92400e' },
};

function StatusChip({ status }) {
    const s = STATUS_COLORS[status] || { bg: '#f1f5f9', color: '#475569' };
    return (
        <Chip
            label={status || '—'}
            size="small"
            sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, bgcolor: s.bg, color: s.color }}
        />
    );
}

const EMPTY_FUND = { amount: '', description: '', origin_payment_method: '' };
const EMPTY_TRANSFER = {
    amount: '', currency: 'usd', destination_payment_method: '',
    description: '', statement_descriptor: '',
};

export default function StripeTreasury() {
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [tab, setTab] = useState(0);

    const [transactions, setTransactions] = useState([]);
    const [outboundPayments, setOutboundPayments] = useState([]);
    const [outboundTransfers, setOutboundTransfers] = useState([]);
    const [inboundTransfers, setInboundTransfers] = useState([]);
    const [receivedCredits, setReceivedCredits] = useState([]);
    const [receivedDebits, setReceivedDebits] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [fundOpen, setFundOpen] = useState(false);
    const [fundForm, setFundForm] = useState({ ...EMPTY_FUND });
    const [funding, setFunding] = useState(false);

    const [transferOpen, setTransferOpen] = useState(false);
    const [transferForm, setTransferForm] = useState({ ...EMPTY_TRANSFER });
    const [transferring, setTransferring] = useState(false);

    const [syncing, setSyncing] = useState(false);
    const [setupStatus, setSetupStatus] = useState(null);
    const [setupLoading, setSetupLoading] = useState(true);

    const fetchSetupStatus = useCallback(async () => {
        setSetupLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/treasury/setup-status');
            if (res.data?.success) {
                setSetupStatus(res.data.data);
            }
        } catch (err) {
            // Even setup-status failing — treat as not onboarded.
            setSetupStatus({
                onboarded: false,
                has_financial_accounts: false,
                onboarding_url: 'https://stripe.com/docs/treasury/access',
                reason: err.response?.data?.message || 'Unable to verify Treasury status',
            });
        } finally {
            setSetupLoading(false);
        }
    }, []);

    const fetchAccounts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/stripe/treasury/accounts');
            if (res.data?.success) {
                const accts = res.data.data || [];
                setAccounts(accts);
                if (!selectedAccount && accts.length > 0) {
                    setSelectedAccount(accts[0]);
                }
            }
        } catch (err) {
            const data = err.response?.data;
            if (data?.code === 'TREASURY_NOT_ONBOARDED') {
                // Surface onboarding banner instead of red error
                setSetupStatus({
                    onboarded: false,
                    has_financial_accounts: false,
                    onboarding_url: data.onboarding_url,
                    reason: data.message,
                });
            } else {
                setError(data?.message || 'Failed to load financial accounts');
            }
        } finally {
            setIsLoading(false);
        }
    }, [selectedAccount]);

    useEffect(() => { fetchSetupStatus(); }, [fetchSetupStatus]);
    useEffect(() => {
        if (setupStatus && setupStatus.onboarded) fetchAccounts();
        else if (setupStatus && !setupStatus.onboarded) setIsLoading(false);
    }, [setupStatus, fetchAccounts]);

    const fetchTab = useCallback(async () => {
        if (!selectedAccount) return;
        if (setupStatus && !setupStatus.onboarded) return;
        setTabLoading(true);
        const params = { financial_account: selectedAccount.id, limit: 50 };
        try {
            if (tab === 0) {
                const res = await axiosInstance.get('/api/finance/stripe/treasury/transactions', { params });
                setTransactions(res.data?.data || []);
            } else if (tab === 1) {
                const res = await axiosInstance.get('/api/finance/stripe/treasury/outbound-payments', { params });
                setOutboundPayments(res.data?.data || []);
            } else if (tab === 2) {
                const res = await axiosInstance.get('/api/finance/stripe/treasury/outbound-transfers', { params });
                setOutboundTransfers(res.data?.data || []);
            } else if (tab === 3) {
                const res = await axiosInstance.get('/api/finance/stripe/treasury/inbound-transfers', { params });
                setInboundTransfers(res.data?.data || []);
            } else if (tab === 4) {
                const res = await axiosInstance.get('/api/finance/stripe/treasury/received-credits', { params });
                setReceivedCredits(res.data?.data || []);
            } else if (tab === 5) {
                const res = await axiosInstance.get('/api/finance/stripe/treasury/received-debits', { params });
                setReceivedDebits(res.data?.data || []);
            }
        } catch (err) {
            const data = err.response?.data;
            if (data?.code === 'TREASURY_NOT_ONBOARDED') {
                setSetupStatus({
                    onboarded: false,
                    has_financial_accounts: false,
                    onboarding_url: data.onboarding_url,
                    reason: data.message,
                });
            } else {
                setError(data?.message || 'Failed to load data');
            }
        } finally {
            setTabLoading(false);
        }
    }, [selectedAccount, tab, setupStatus]);

    useEffect(() => { fetchTab(); }, [fetchTab]);

    const handleFund = async () => {
        if (!selectedAccount) return;
        setFunding(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/stripe/treasury/inbound-transfers', {
                financial_account: selectedAccount.id,
                amount: Math.round(parseFloat(fundForm.amount) * 100),
                currency: 'usd',
                origin_payment_method: fundForm.origin_payment_method,
                description: fundForm.description || undefined,
            });
            setSuccess('Inbound transfer initiated');
            setFundOpen(false);
            setFundForm({ ...EMPTY_FUND });
            await fetchAccounts();
            if (tab === 3) await fetchTab();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initiate transfer');
        } finally {
            setFunding(false);
        }
    };

    const handleOutboundTransfer = async () => {
        if (!selectedAccount) return;
        setTransferring(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/stripe/treasury/outbound-transfers', {
                financial_account: selectedAccount.id,
                amount: Math.round(parseFloat(transferForm.amount) * 100),
                currency: transferForm.currency || 'usd',
                destination_payment_method: transferForm.destination_payment_method,
                description: transferForm.description || undefined,
                statement_descriptor: transferForm.statement_descriptor || undefined,
            });
            setSuccess('Outbound transfer initiated');
            setTransferOpen(false);
            setTransferForm({ ...EMPTY_TRANSFER });
            await fetchAccounts();
            if (tab === 2) await fetchTab();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initiate transfer');
        } finally {
            setTransferring(false);
        }
    };

    const handleSyncStatuses = async () => {
        setSyncing(true);
        setError(null);
        try {
            if (tab === 1 && outboundPayments.length > 0) {
                const res = await axiosInstance.post(
                    '/api/finance/stripe/treasury/outbound-payments/sync',
                    { payment_ids: outboundPayments.map(p => p.id) },
                );
                const fresh = res.data?.data || {};
                setOutboundPayments(prev => prev.map(p => fresh[p.id]
                    ? { ...p, status: fresh[p.id].status }
                    : p));
                setSuccess(`Synced ${Object.keys(fresh).length} payment status${Object.keys(fresh).length === 1 ? '' : 'es'}`);
            } else if (tab === 3 && inboundTransfers.length > 0) {
                const res = await axiosInstance.post(
                    '/api/finance/stripe/treasury/inbound-transfers/sync',
                    { transfer_ids: inboundTransfers.map(t => t.id) },
                );
                const fresh = res.data?.data || {};
                setInboundTransfers(prev => prev.map(t => fresh[t.id]
                    ? { ...t, status: fresh[t.id].status }
                    : t));
                setSuccess(`Synced ${Object.keys(fresh).length} transfer status${Object.keys(fresh).length === 1 ? '' : 'es'}`);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    const handleCancelOutboundPayment = async (id) => {
        if (!window.confirm(`Cancel OutboundPayment ${id}? This is only possible while processing.`)) return;
        try {
            await axiosInstance.post(`/api/finance/stripe/treasury/outbound-payments/${id}/cancel`);
            setSuccess('OutboundPayment cancelled');
            await fetchTab();
        } catch (err) {
            setError(err.response?.data?.message || 'Cancel failed');
        }
    };

    const handleCancelInboundTransfer = async (id) => {
        if (!window.confirm(`Cancel InboundTransfer ${id}?`)) return;
        try {
            await axiosInstance.post(`/api/finance/stripe/treasury/inbound-transfers/${id}/cancel`);
            setSuccess('InboundTransfer cancelled');
            await fetchTab();
        } catch (err) {
            setError(err.response?.data?.message || 'Cancel failed');
        }
    };

    const handleCancelOutboundTransfer = async (id) => {
        if (!window.confirm(`Cancel OutboundTransfer ${id}?`)) return;
        try {
            await axiosInstance.post(`/api/finance/stripe/treasury/outbound-transfers/${id}/cancel`);
            setSuccess('OutboundTransfer cancelled');
            await fetchTab();
        } catch (err) {
            setError(err.response?.data?.message || 'Cancel failed');
        }
    };

    const balanceCards = (() => {
        if (!selectedAccount?.balance) return [];
        const b = selectedAccount.balance;
        return [
            { label: 'Cash Available', amount: b.cash?.usd || 0, color: ACCENT },
            { label: 'Inbound Pending', amount: b.inbound_pending?.usd || 0, color: '#3b82f6' },
            { label: 'Outbound Pending', amount: b.outbound_pending?.usd || 0, color: '#f59e0b' },
        ];
    })();

    const canSync = (tab === 1 && outboundPayments.length > 0) ||
                    (tab === 3 && inboundTransfers.length > 0);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Stripe Treasury
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', mt: 0.25 }}>
                                Financial accounts, transfers & payments
                            </Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => { fetchAccounts(); fetchTab(); }}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }, color: '#fff', boxShadow: 'none' }}>
                            Refresh
                        </Button>
                        {canSync && (
                            <Button variant="contained"
                                startIcon={syncing ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SyncIcon />}
                                onClick={handleSyncStatuses}
                                disabled={syncing}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }, color: '#fff', boxShadow: 'none' }}>
                                Sync Status
                            </Button>
                        )}
                        <Button variant="contained" startIcon={<SwapHorizIcon />} onClick={() => setTransferOpen(true)} disabled={!selectedAccount}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}>
                            Outbound Transfer
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFundOpen(true)} disabled={!selectedAccount}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}>
                            Fund Account
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Onboarding banner — short-circuits the page when Treasury isn't enabled */}
                {!setupLoading && setupStatus && !setupStatus.onboarded && (
                    <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2.5}>
                            <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <LockIcon sx={{ fontSize: 28, color: '#92400e' }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.05rem', color: '#78350f' }}>
                                    Stripe Treasury isn't enabled on this account yet
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#92400e', mt: 0.5, lineHeight: 1.5 }}>
                                    {setupStatus.reason || 'Complete Stripe Treasury onboarding to start using financial accounts, transfers, payments, sweeps, and embedded banking.'}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#a16207', mt: 1.5 }}>
                                    Treasury powers AP bill pay, payroll direct deposit, expense reimbursement, tax remittance, inter-entity transfers, and the cash management sweep automation.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    endIcon={<LaunchIcon />}
                                    component="a"
                                    href={setupStatus.onboarding_url || 'https://stripe.com/docs/treasury/access'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: '#92400e', '&:hover': { bgcolor: '#78350f' } }}
                                >
                                    Start Onboarding
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => { fetchSetupStatus(); }}
                                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#92400e', color: '#92400e' }}
                                >
                                    Re-check
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                )}

                {/* Hide everything else until onboarded */}
                {!setupLoading && setupStatus && !setupStatus.onboarded ? null : (<>

                {/* Account selector */}
                {accounts.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {accounts.map(acct => (
                            <Chip key={acct.id} label={acct.id}
                                onClick={() => setSelectedAccount(acct)}
                                sx={{
                                    fontFamily, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                                    bgcolor: selectedAccount?.id === acct.id ? '#dcfce7' : '#f1f5f9',
                                    color: selectedAccount?.id === acct.id ? '#166534' : '#64748b',
                                    border: selectedAccount?.id === acct.id ? '1px solid #22c55e' : '1px solid #e2e8f0',
                                }} />
                        ))}
                    </Box>
                )}

                {/* Balance cards */}
                {isLoading ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                        {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: '14px' }} />)}
                    </Box>
                ) : selectedAccount && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                        {balanceCards.map(card => (
                            <Paper key={card.label} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: card.color, mt: 0.5 }}>
                                    {formatCurrency(card.amount)}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Tabs */}
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                        '& .Mui-selected': { color: `${ACCENT} !important` },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT },
                    }}>
                    <Tab label="Transactions" />
                    <Tab label={`Outbound Payments${outboundPayments.length ? ` (${outboundPayments.length})` : ''}`} />
                    <Tab label={`Outbound Transfers${outboundTransfers.length ? ` (${outboundTransfers.length})` : ''}`} />
                    <Tab label={`Inbound Transfers${inboundTransfers.length ? ` (${inboundTransfers.length})` : ''}`} />
                    <Tab label={`Received Credits${receivedCredits.length ? ` (${receivedCredits.length})` : ''}`} />
                    <Tab label={`Received Debits${receivedDebits.length ? ` (${receivedDebits.length})` : ''}`} />
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        {tab === 0 && <SimpleTable
                            headers={['Transaction ID', 'Amount', 'Type', 'Status', 'Created']}
                            loading={tabLoading}
                            rows={transactions}
                            renderRow={(tx) => (
                                <TableRow key={tx.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{tx.id}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: (tx.amount || 0) >= 0 ? '#166534' : '#991b1b' }}>
                                        {formatCurrency(tx.amount, tx.currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', textTransform: 'capitalize' }}>
                                        {(tx.flow_type || '').replace(/_/g, ' ') || '—'}
                                    </TableCell>
                                    <TableCell><StatusChip status={tx.status} /></TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{formatDate(tx.created)}</TableCell>
                                </TableRow>
                            )}
                        />}

                        {tab === 1 && <SimpleTable
                            headers={['Payment ID', 'Amount', 'Description', 'Destination', 'Status', 'Expected', 'Created', '']}
                            loading={tabLoading}
                            rows={outboundPayments}
                            renderRow={(p) => (
                                <TableRow key={p.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.id}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700 }}>
                                        {formatCurrency(p.amount, p.currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{p.description || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.78rem', fontFamily: 'monospace', color: '#64748b' }}>
                                        {p.destination_payment_method || '—'}
                                    </TableCell>
                                    <TableCell><StatusChip status={p.status} /></TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.78rem' }}>
                                        {p.expected_arrival_date ? new Date(p.expected_arrival_date * 1000).toLocaleDateString() : '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{formatDate(p.created)}</TableCell>
                                    <TableCell>
                                        {p.status === 'processing' && (
                                            <Tooltip title="Cancel">
                                                <IconButton size="small" onClick={() => handleCancelOutboundPayment(p.id)}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        />}

                        {tab === 2 && <SimpleTable
                            headers={['Transfer ID', 'Amount', 'Description', 'Destination', 'Status', 'Created', '']}
                            loading={tabLoading}
                            rows={outboundTransfers}
                            renderRow={(t) => (
                                <TableRow key={t.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{t.id}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700 }}>
                                        {formatCurrency(t.amount, t.currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{t.description || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>
                                        {t.destination_payment_method || '—'}
                                    </TableCell>
                                    <TableCell><StatusChip status={t.status} /></TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{formatDate(t.created)}</TableCell>
                                    <TableCell>
                                        {t.status === 'processing' && (
                                            <Tooltip title="Cancel">
                                                <IconButton size="small" onClick={() => handleCancelOutboundTransfer(t.id)}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        />}

                        {tab === 3 && <SimpleTable
                            headers={['Transfer ID', 'Amount', 'Description', 'Origin', 'Status', 'Created', '']}
                            loading={tabLoading}
                            rows={inboundTransfers}
                            renderRow={(t) => (
                                <TableRow key={t.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{t.id}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>
                                        {formatCurrency(t.amount, t.currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{t.description || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>
                                        {t.origin_payment_method || '—'}
                                    </TableCell>
                                    <TableCell><StatusChip status={t.status} /></TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{formatDate(t.created)}</TableCell>
                                    <TableCell>
                                        {t.status === 'processing' && (
                                            <Tooltip title="Cancel">
                                                <IconButton size="small" onClick={() => handleCancelInboundTransfer(t.id)}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        />}

                        {tab === 4 && <SimpleTable
                            headers={['Credit ID', 'Amount', 'Description', 'Network', 'Status', 'Received']}
                            loading={tabLoading}
                            rows={receivedCredits}
                            renderRow={(r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <CallReceivedIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                            <span>{r.id}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>
                                        {formatCurrency(r.amount, r.currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{r.description || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.78rem', textTransform: 'uppercase' }}>{r.network || '—'}</TableCell>
                                    <TableCell><StatusChip status={r.status} /></TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{formatDate(r.created)}</TableCell>
                                </TableRow>
                            )}
                        />}

                        {tab === 5 && <SimpleTable
                            headers={['Debit ID', 'Amount', 'Description', 'Network', 'Status', 'Received']}
                            loading={tabLoading}
                            rows={receivedDebits}
                            renderRow={(r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <CallMadeIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                            <span>{r.id}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#991b1b' }}>
                                        {formatCurrency(r.amount, r.currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{r.description || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.78rem', textTransform: 'uppercase' }}>{r.network || '—'}</TableCell>
                                    <TableCell><StatusChip status={r.status} /></TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{formatDate(r.created)}</TableCell>
                                </TableRow>
                            )}
                        />}
                    </TableContainer>
                </Card>

                </>)}
            </Box>

            {/* Fund Account Dialog */}
            <Dialog open={fundOpen} onClose={() => setFundOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Fund Treasury Account</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Amount" type="number" value={fundForm.amount}
                        onChange={e => setFundForm(prev => ({ ...prev, amount: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 1, step: '0.01' },
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Origin Payment Method ID (pm_...)" value={fundForm.origin_payment_method}
                        onChange={e => setFundForm(prev => ({ ...prev, origin_payment_method: e.target.value }))}
                        fullWidth size="small" required
                        helperText="External bank PaymentMethod that will fund the FA"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Description (optional)" value={fundForm.description}
                        onChange={e => setFundForm(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setFundOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleFund}
                        disabled={funding || !fundForm.amount || !fundForm.origin_payment_method}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {funding ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Fund'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Outbound Transfer Dialog */}
            <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Outbound Transfer</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                        Move funds from this Treasury FA to another FA or external destination.
                        Used for inter-entity settlement and ad-hoc treasury transfers.
                    </Typography>
                    <TextField label="Amount" type="number" value={transferForm.amount}
                        onChange={e => setTransferForm(prev => ({ ...prev, amount: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 1, step: '0.01' },
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                    <TextField label="Destination Payment Method ID (pm_... or fa_...)" value={transferForm.destination_payment_method}
                        onChange={e => setTransferForm(prev => ({ ...prev, destination_payment_method: e.target.value }))}
                        fullWidth size="small" required
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                    <TextField label="Statement Descriptor (max 10)" value={transferForm.statement_descriptor}
                        onChange={e => setTransferForm(prev => ({ ...prev, statement_descriptor: e.target.value.slice(0, 10) }))}
                        fullWidth size="small"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                    <TextField label="Description (optional)" value={transferForm.description}
                        onChange={e => setTransferForm(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setTransferOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleOutboundTransfer}
                        disabled={transferring || !transferForm.amount || !transferForm.destination_payment_method}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {transferring ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send Transfer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function SimpleTable({ headers, loading, rows, renderRow }) {
    return (
        <Table size="small">
            <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {headers.map(h => (
                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>
                            {h}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {loading ? (
                    [1, 2, 3].map(i => (
                        <TableRow key={i}>
                            <TableCell colSpan={headers.length}><Skeleton height={32} /></TableCell>
                        </TableRow>
                    ))
                ) : rows.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={headers.length} sx={{ textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No records found</Typography>
                        </TableCell>
                    </TableRow>
                ) : rows.map(renderRow)}
            </TableBody>
        </Table>
    );
}
